import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import {
  enableFullTranslations,
  errors,
  I18nApi,
  isCurrentCompanyCustomTranslationsLoad,
  isFullCatalogueLoaded,
  Log,
  setCompanyCustomTranslations,
  startCompanyCustomTranslationsLoad,
  type ICustomTranslation,
} from "@ryde/shared";

export const useCustomTranslationsActions = () => {
  const { t, i18n } = useTranslation();

  /** Resolves false when another company was selected meanwhile: that selection owns the labels and the scope. */
  const handleCustomTranslations = useEvent(
    async (companyId: number): Promise<boolean> => {
      const loadId = startCompanyCustomTranslationsLoad();
      // Without this company's overrides the previous company's must not stay behind.
      let customTranslations: ICustomTranslation[] = [];

      try {
        // The overrides are merged into the full catalogue, and a catalogue arriving after them would replace them.
        await enableFullTranslations(i18n);

        if (
          isFullCatalogueLoaded({
            i18nInstance: i18n,
            languages: [i18n.language],
          })
        ) {
          customTranslations = await I18nApi.getCustomTranslations(companyId);
          Log.debug("Loaded Custom Translations", customTranslations);
        } else {
          Log.warn(
            "Custom translations skipped: the translation catalogue did not load",
          );
        }
      } catch (error) {
        Log.warnAndToast(t(errors.common.technicalProblemOccurred), error);
      }

      if (!isCurrentCompanyCustomTranslationsLoad(loadId)) {
        return false;
      }

      setCompanyCustomTranslations({
        i18nInstance: i18n,
        translations: customTranslations,
      });
      return true;
    },
  );

  return {
    handleCustomTranslations,
  };
};
